import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Box,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

type Phase = "uploading" | "extracting";

interface AnalyzingProgressProps {
  phase: Phase;
  compact?: boolean;
}

const TIPS_EXTRACTING = [
  "Reading the document page by page.",
  "Large EOBs / EOPs can take 20–30 seconds.",
  "Detecting payer, payment, claims, and procedure structure.",
  "Producing structured JSON we can map fields against.",
  "Almost there — finalizing the extraction.",
];

const TIPS_UPLOADING = [
  "Uploading PDF to the server…",
  "Computing file hash for dedup…",
];

const STEPS: { id: Phase; label: string }[] = [
  { id: "uploading", label: "Upload PDF" },
  { id: "extracting", label: "Extract structured data" },
];

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem}s`;
}

export default function AnalyzingProgress({
  phase,
  compact = false,
}: AnalyzingProgressProps) {
  const [tipIdx, setTipIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const startedAt = React.useRef<number>(Date.now());

  // Reset elapsed when phase transitions in/out (mounts).
  useEffect(() => {
    startedAt.current = Date.now();
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setElapsed(Date.now() - startedAt.current);
    }, 500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const tips = phase === "uploading" ? TIPS_UPLOADING : TIPS_EXTRACTING;
    setTipIdx(0);
    const id = setInterval(() => {
      setTipIdx((i) => (i + 1) % tips.length);
    }, 4500);
    return () => clearInterval(id);
  }, [phase]);

  const tips = phase === "uploading" ? TIPS_UPLOADING : TIPS_EXTRACTING;
  const currentStepIdx = phase === "uploading" ? 0 : 1;

  return (
    <Stack
      spacing={compact ? 1.5 : 2.5}
      sx={{
        padding: compact ? 2 : 3,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "#FBFAFF",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <AutoAwesomeIcon sx={{ color: "primary.main", fontSize: 22 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Analyzing your document
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {phase === "uploading"
              ? "Sending the PDF to the server."
              : "Extracting structured data from the PDF."}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontFamily: '"Roboto Mono", monospace',
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          {formatElapsed(elapsed)}
        </Typography>
      </Stack>

      <LinearProgress sx={{ borderRadius: 1, height: 6 }} />

      <Stack spacing={1}>
        {STEPS.map((step, idx) => {
          const done = idx < currentStepIdx;
          const active = idx === currentStepIdx;
          let labelColor = "text.secondary";
          if (active) labelColor = "text.primary";
          else if (done) labelColor = "success.dark";
          return (
            <Stack
              key={step.id}
              direction="row"
              spacing={1.25}
              alignItems="center"
            >
              {done && (
                <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} />
              )}
              {active && <CircularProgress size={14} thickness={5} />}
              {!done && !active && (
                <RadioButtonUncheckedIcon
                  sx={{ color: "text.disabled", fontSize: 18 }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: active ? 600 : 400,
                  color: labelColor,
                }}
              >
                {step.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>

      <Box
        sx={{
          padding: 1.25,
          backgroundColor: "#F5F3FF",
          borderRadius: 1,
          borderLeft: "3px solid",
          borderColor: "primary.light",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {tips[tipIdx]}
        </Typography>
      </Box>
    </Stack>
  );
}
